import MapBaseClass from "./MapBaseClass.js";
import Vector from "./Vector.js";

export default class MapLink extends MapBaseClass {
    get ignoredFields() {
        return [`map`, `masterItem`, `slaveItem`];
    }

    constructor(data, map) {
        super(data);
        this.map = map;

        this.floorId = null;
        this.name = null;
        this.type = `navigation`;
        this.color = `inherit`;
        this.distance = 0;
        this.angle = 0;

        this.visible = true;
        this.dashed = false;
        this.optional = false;

        // Define if the cost of taking this path should be higher
        // recommended | default | not-recommended | avoid | unavailable
        this.navRule = `default`;
        // NavCost is based on distance and navRule
        this.navCost = 0;

        // from slave to master
        this.direction = 1;

        this.master = {
            id: null,
            plug: ``,
            plugType: ``
        };
        this.masterItem = null;

        this.slave = {
            id: null,
            plug: ``,
            plugType: ``
        };
        this.slaveItem = null;

        if (data && data.master && data.master.id) {
            this.loadSaveCompletely(data);
            this.masterItem = map.getItemById(data.master.id);
            this.slaveItem = map.getItemById(data.slave.id);
            this.linkItems(this.masterItem, this.slaveItem);
        }
    }

    applyCompatibility(data) {
        if (data.floor_id) {
            this.floorId = data.floor_id;
            delete this.floor_id;
        }
    }

    linkItems(itemA, itemB) {
        this.setMasterItem(itemA);
        this.setSlaveItem(itemB);
        if (itemA && itemB) {
            this.floorId = itemA.floorId;
            this.updateCoordinates();
            this.updateNavCost();
        } else {
            console.error(`Missing item for link ${this.name}`);
        }
    }

    getMiddlePoint() {
        return {
            x: (this.masterItem.x + this.slaveItem.x) / 2,
            y: (this.masterItem.y + this.slaveItem.y) / 2
        }
    }

    getVector() {
        let vector;
        if (this.direction === 1) {
            vector = new Vector(this.slaveItem.x - this.masterItem.x, this.masterItem.y - this.slaveItem.y);
        } else {
            vector = new Vector(this.masterItem.x - this.slaveItem.x, this.slaveItem.y - this.masterItem.y);
        }
        vector.invertY();
        return vector;
    }

    updateCoordinates() {
        const vector = this.getVector();
        this.distance = vector.getLength();
        this.setEndPoint(this.slaveItem, this.masterItem);
    }

    updateNavCost() {
        this.navCost = 0;
        switch (this.navRule) {
            case `recommended`:
                this.navCost -= this.distance * 0.5;
                break;
            case `not-recommended`:
                this.navCost += this.distance * 1.25;
                break;
            case `avoid`:
                this.navCost += this.distance * 2;
                break;
            case `unavailable`:
                this.navCost += this.distance * 99;
                break;
        }
    }

    setEndPoint(endItem) {
        if (endItem.id === this.masterItem.id) {
            this.direction = 1;
        } else {
            this.direction = -1;
        }
        const vector = this.getVector();
        this.angle =  vector.getAngle();
    }

    getStartItem() {
        return this.direction > 0 ? this.slaveItem : this.masterItem;
    }

    getEndItem() {
        return this.direction < 0 ? this.slaveItem : this.masterItem;
    }

    isLinkBetween(itemA, itemB) {
        return (this.masterItem.id === itemA.id || this.masterItem.id === itemB.id) &&
            (this.slaveItem.id === itemA.id || this.slaveItem.id === itemB.id);
    }

    getOtherItem(item) {
        if (this.masterItem.id === item.id) {
            return this.slaveItem;
        } else  if (this.slaveItem.id === item.id) {
            return this.masterItem;
        }
    }

    isLinkOnFloor(floorId) {
        return this.masterItem.isOnFloor(floorId) &&
            this.slaveItem.isOnFloor(floorId);
    }

    setMasterItem(item) {
        if (this.masterItem) {
            this.masterItem.removeLink(this);
        }

        this.masterItem = item;
        if (!item) {
            this.master.id = null;
        } else {
            this.master.id = item.id;
            this.updateName();

            if (!item.addLink(this)) {
                return false;
            }
        }
    }

    setSlaveItem(item) {
        if (this.slaveItem) {
            this.slaveItem.removeLink(this);
        }

        this.slaveItem = item;
        if (!item) {
            this.slave.id = null;
        } else {
            this.slave.id = item.id;
            this.updateName();

            if (!item.addLink(this)) {
                return false;
            }
        }
    }

    updateName() {
        if (this.masterItem && this.slaveItem) {
            this.name = `Link ${this.masterItem.name} - ${this.slaveItem.name}`;
        }
    }

    beforeDelete() {
        this.masterItem.removeLink(this);
        this.slaveItem.removeLink(this);
    }
}
